/**
 * /operations/transactions/overtime/new — Overtime HR-initiate
 * form (M30.5.4).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import { TransactionFormShell } from "../../components/hr_operations";
import { api, type EmployeePickerOption } from "../../lib/api";
import { useHrActor } from "../../lib/use-hr-actor";
import { useTransactionAssist } from "../../lib/use-transaction-assist";

export function OvertimeHrInitiatePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [docUrl, setDocUrl] = useState<string>("");
  const [attendanceDate, setAttendanceDate] = useState<string>("");
  const [overtimeMinutes, setOvertimeMinutes] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reasonCodes = useQuery({
    queryKey: ["reason-codes", "overtime"],
    queryFn: () => api.listReasonCodes("overtime"),
    staleTime: 5 * 60_000,
  });

  // Actor identity from Clerk (audit fix C1 — commandment 39).
  const actor = useHrActor();

  const enabled =
    actor.isReady &&
    !!employee &&
    !!reasonCode &&
    !!attendanceDate &&
    !!overtimeMinutes;
  const body = {
    actor_user_id: actor.actorUserId,
    actor_role: actor.actorRole,
    target_employee_id: employee?.id ?? "",
    reason_code: reasonCode,
    reason_explanation: explanation || null,
    supporting_document_url: docUrl || null,
    attendance_date: attendanceDate,
    overtime_minutes: Number(overtimeMinutes || 0),
    notes: null,
  };
  const { result, isLoading, error, acknowledgedCodes, acknowledge } =
    useTransactionAssist({
      body,
      enabled,
      callAssist: (b) => api.overtimeHrInitiateAssist(b),
    });

  async function submit() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.overtimeHrInitiateCommit({
        ...body,
        ai_findings_acknowledged: Array.from(acknowledgedCodes),
      });
      setSubmitSuccess(true);
      setTimeout(
        () => navigate("/operations/payroll-candidates"),
        1500,
      );
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <TransactionFormShell
      transactionType="overtime"
      title={t("operations.overtime_form_title")}
      assist={result}
      isAssistLoading={isLoading}
      assistError={error}
      acknowledgedCodes={acknowledgedCodes}
      onAcknowledge={acknowledge}
      onAcceptSuggestion={(field, value) => {
        if (field === "reason_explanation" && typeof value === "string") {
          setExplanation(value);
        }
        if (
          field === "overtime_minutes" &&
          typeof value === "number"
        ) {
          setOvertimeMinutes(String(value));
        }
      }}
      onSubmit={submit}
      isSubmitting={isSubmitting || !actor.isReady}
      submitSuccess={submitSuccess}
      submitError={submitError}
    >
      <Field label={t("operations.form_employee_label")}>
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </Field>
      <Field label={t("operations.form_reason_code_label")}>
        <select
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          <option value="">—</option>
          {reasonCodes.data?.codes.map((c) => (
            <option key={c.code} value={c.code}>
              {isAr ? c.display_ar : c.display_en}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("operations.overtime_form_date")}>
          <input
            type="date"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={attendanceDate}
            onChange={(e) => setAttendanceDate(e.target.value)}
          />
        </Field>
        <Field label={t("operations.overtime_form_minutes")}>
          <input
            type="number"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={overtimeMinutes}
            onChange={(e) => setOvertimeMinutes(e.target.value)}
            min={0}
            max={720}
            dir="ltr"
          />
        </Field>
      </div>
      <Field label={t("operations.form_reason_explanation_label")}>
        <textarea
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          rows={3}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>
      <Field label={t("operations.form_supporting_doc_label")}>
        <input
          type="url"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={docUrl}
          onChange={(e) => setDocUrl(e.target.value)}
          placeholder={t("operations.form_supporting_doc_placeholder")}
          dir="ltr"
        />
      </Field>
    </TransactionFormShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
